/**
 * Dynamic Synth-Rock Music Engine for Zelvis Antigravity
 * Synthesizes real-time rock & chiptune background music tracks using Web Audio API:
 * - Level 1: "Olympus Rock Anthem" (Energetic classic rock groove with heavy bass & power leads)
 * - Level 2: "Tartarus Metal Rave" (Fast-paced aggressive synth-metal with driving 16th bass & neon arpeggios)
 */
export class MusicEngine {
  constructor(soundEffects) {
    this.sfx = soundEffects;
    this.isPlaying = false;
    this.currentTrack = null;
    this.timerId = null;
    this.step = 0;
    this.bpm = 132;
    this.isMuted = false;
  }

  get audioCtx() {
    return this.sfx.ctx;
  }

  setMuted(muted) {
    this.isMuted = muted;
    if (muted && this.isPlaying) {
      this.pause();
    } else if (!muted && !this.isPlaying && this.currentTrack) {
      this.play(this.currentTrack);
    }
  }

  play(trackName = 'olympus') {
    if (this.isMuted) return;
    this.stop();

    this.sfx.init();
    this.currentTrack = trackName;
    this.isPlaying = true;
    this.step = 0;

    if (trackName === 'olympus') {
      this.bpm = 130;
    } else if (trackName === 'tartarus') {
      this.bpm = 150;
    } else if (trackName === 'hephaestus') {
      this.bpm = 142;
    } else if (trackName === 'cosmic') {
      this.bpm = 160;
    } else if (trackName === 'atlantis') {
      this.bpm = 148;
    } else if (trackName === 'dionysus') {
      this.bpm = 156;
    } else if (trackName === 'ares') {
      this.bpm = 168;
    } else if (trackName === 'chronos') {
      this.bpm = 180;
    } else if (trackName === 'styx') {
      this.bpm = 152;
    } else if (trackName === 'apollo') {
      this.bpm = 172;
    } else if (trackName === 'chaos') {
      this.bpm = 192;
    } else {
      this.bpm = 135;
    }

    const interval = (60 / this.bpm / 4) * 1000; // 16th note interval in ms
    this.timerId = setInterval(() => {
      this.tick();
    }, interval);
  }

  stop() {
    this.isPlaying = false;
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    this.step = 0;
  }

  pause() {
    this.isPlaying = false;
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  tick() {
    try {
      const ctx = this.audioCtx;
      if (!ctx || ctx.state === 'suspended' || this.isMuted) return;

      const time = ctx.currentTime + 0.03;

      if (this.currentTrack === 'olympus') {
        this.tickOlympus(time, this.step);
      } else if (this.currentTrack === 'tartarus') {
        this.tickTartarus(time, this.step);
      } else if (this.currentTrack === 'hephaestus') {
        this.tickHephaestus(time, this.step);
      } else if (this.currentTrack === 'cosmic') {
        this.tickCosmic(time, this.step);
      } else if (this.currentTrack === 'atlantis') {
        this.tickAtlantis(time, this.step);
      } else if (this.currentTrack === 'dionysus') {
        this.tickDionysus(time, this.step);
      } else if (this.currentTrack === 'ares') {
        this.tickAres(time, this.step);
      } else if (this.currentTrack === 'chronos') {
        this.tickChronos(time, this.step);
      } else if (this.currentTrack === 'styx') {
        this.tickStyx(time, this.step);
      } else if (this.currentTrack === 'apollo') {
        this.tickApollo(time, this.step);
      } else if (this.currentTrack === 'chaos') {
        this.tickChaos(time, this.step);
      } else {
        this.tickOlympus(time, this.step);
      }

      this.step = (this.step + 1) % 64; // 4-bar loop
    } catch {
      // Ignore audio glitch to prevent game freezing
    }
  }

  // =========================================================================
  // LEVEL 1: OLYMPUS ROCK ANTHEM
  // =========================================================================
  tickOlympus(time, s) {
    const barStep = s % 16;

    // 1. DRUMS: Kick on 0, 8; Snare on 4, 12; Hi-hat on even steps
    if (barStep === 0 || barStep === 8 || (barStep === 14 && Math.random() < 0.4)) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time);
    }
    if (barStep % 2 === 0) {
      this.playHiHat(time, barStep % 4 === 2 ? 0.08 : 0.04);
    }

    // 2. BASSLINE: Rock 8th notes (Roots in A minor / C major: A, C, D, E)
    // 64-step sequence
    const bassPatterns = [
      // Bar 1: A (110 Hz)
      110, 0, 110, 0, 110, 0, 130.8, 0, 110, 0, 110, 0, 146.8, 0, 164.8, 0,
      // Bar 2: C (130.8 Hz)
      130.8, 0, 130.8, 0, 130.8, 0, 146.8, 0, 130.8, 0, 164.8, 0, 146.8, 0, 130.8, 0,
      // Bar 3: D (146.8 Hz)
      146.8, 0, 146.8, 0, 146.8, 0, 164.8, 0, 146.8, 0, 174.6, 0, 164.8, 0, 146.8, 0,
      // Bar 4: E -> G turnaround (164.8 Hz -> 196 Hz)
      164.8, 0, 164.8, 0, 164.8, 0, 196.0, 0, 196.0, 0, 164.8, 0, 146.8, 0, 130.8, 0
    ];

    const bassFreq = bassPatterns[s];
    if (bassFreq > 0) {
      this.playBassTone(time, bassFreq, 0.14, 'sawtooth');
    }

    // 3. LEAD MELODY: Rock riff on bars 2 and 4
    const leadPatterns = [
      // Bar 1: rest
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      // Bar 2: Riff 1
      440, 0, 523.2, 0, 587.3, 0, 659.2, 659.2, 0, 0, 587.3, 0, 523.2, 0, 440, 0,
      // Bar 3: rest
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      // Bar 4: Heroic Riff 2
      659.2, 0, 659.2, 0, 783.9, 0, 880, 0, 783.9, 0, 659.2, 0, 587.3, 523.2, 440, 0
    ];

    const leadFreq = leadPatterns[s];
    if (leadFreq > 0) {
      this.playLeadTone(time, leadFreq, 0.16, 'square');
    }
  }

  // =========================================================================
  // LEVEL 2: TARTARUS METAL RAVE
  // =========================================================================
  tickTartarus(time, s) {
    const barStep = s % 16;

    // Fast Double-Kick & Heavy Snare
    if (barStep === 0 || barStep === 2 || barStep === 8 || barStep === 10) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time, 0.3);
    }
    // Continuous 16th Hi-hats
    this.playHiHat(time, (barStep % 4 === 2) ? 0.07 : 0.03);

    // Fast Galloping Metal Bass (E minor / D / C / B) - 82.4 Hz
    const tartarusBass = [
      // Bar 1: E gallop
      82.4, 82.4, 0, 82.4, 82.4, 82.4, 0, 82.4, 82.4, 82.4, 0, 82.4, 98.0, 0, 82.4, 0,
      // Bar 2: G / F# shift
      98.0, 98.0, 0, 98.0, 92.5, 92.5, 0, 92.5, 82.4, 82.4, 0, 82.4, 110.0, 0, 98.0, 0,
      // Bar 3: C / D power
      130.8, 0, 130.8, 0, 146.8, 0, 146.8, 0, 82.4, 82.4, 0, 82.4, 82.4, 82.4, 0, 82.4,
      // Bar 4: Fast B climax
      123.4, 123.4, 123.4, 0, 123.4, 123.4, 0, 123.4, 146.8, 0, 130.8, 0, 123.4, 0, 98.0, 0
    ];

    const bFreq = tartarusBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.1, 'sawtooth');
    }

    // Fast Cyberpunk Synth Arpeggio
    const arpNotes = [329.6, 392.0, 493.8, 659.2]; // Em chord
    const arpNote = arpNotes[s % 4];
    if (s % 2 === 0) {
      this.playLeadTone(time, arpNote * (s >= 32 ? 1.5 : 1.0), 0.08, 'sawtooth');
    }
  }

  // =========================================================================
  // LEVEL 3: HEPHAESTUS CYBER-FORGE INDUSTRIAL ROCK
  // =========================================================================
  tickHephaestus(time, s) {
    const barStep = s % 16;

    // Heavy Industrial Stomp: Kick on 0, 6, 8, 14
    if (barStep === 0 || barStep === 6 || barStep === 8 || barStep === 14) {
      this.playKick(time);
    }
    // Gated Industrial Snare on 4, 12
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time, 0.28);
    }
    // Metallic Clang Hi-Hat
    if (barStep % 2 === 0) {
      this.playHiHat(time, barStep % 4 === 2 ? 0.09 : 0.04);
    }

    // Heavy Industrial Bronze Bass (D minor / F / G / A#) - 73.4 Hz
    const hephBass = [
      // Bar 1: D
      73.4, 0, 73.4, 73.4, 0, 73.4, 0, 87.3, 73.4, 0, 73.4, 73.4, 0, 98.0, 0, 87.3,
      // Bar 2: F -> G
      87.3, 0, 87.3, 0, 98.0, 0, 98.0, 0, 87.3, 0, 98.0, 0, 110.0, 0, 98.0, 0,
      // Bar 3: D heavy
      73.4, 0, 73.4, 73.4, 0, 73.4, 0, 87.3, 73.4, 0, 73.4, 73.4, 0, 116.5, 0, 98.0,
      // Bar 4: A# -> C turnaround
      116.5, 0, 116.5, 0, 116.5, 0, 130.8, 0, 130.8, 0, 116.5, 0, 98.0, 0, 87.3, 0
    ];

    const bFreq = hephBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.12, 'sawtooth');
    }

    // Molten Cyber Guitar Lead Riff on bars 2 and 4
    const hephLead = [
      // Bar 1
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      // Bar 2: Anvil Solo
      293.7, 0, 349.2, 0, 392.0, 0, 440.0, 440.0, 0, 0, 392.0, 0, 349.2, 0, 293.7, 0,
      // Bar 3
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      // Bar 4: Shred Climax
      466.2, 0, 466.2, 0, 523.3, 0, 587.3, 0, 523.3, 0, 466.2, 0, 392.0, 349.2, 293.7, 0
    ];

    const lFreq = hephLead[s];
    if (lFreq > 0) {
      this.playLeadTone(time, lFreq, 0.14, 'square');
    }
  }

  // =========================================================================
  // LEVEL 4: COSMIC OLYMPUS SPEED METAL / APOTHEOSIS
  // =========================================================================
  tickCosmic(time, s) {
    const barStep = s % 16;

    // Relentless Double-Bass Drums & Power Snare
    if (barStep % 2 === 0) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time, 0.32);
    }
    this.playHiHat(time, 0.06);

    // Cosmic Galloping Bass in B minor (123.5 Hz)
    const cosmicBass = [
      // Bar 1: B
      123.5, 123.5, 0, 123.5, 123.5, 0, 146.8, 123.5, 123.5, 123.5, 0, 123.5, 164.8, 0, 146.8, 0,
      // Bar 2: D -> E
      146.8, 146.8, 0, 146.8, 164.8, 164.8, 0, 164.8, 146.8, 0, 164.8, 0, 185.0, 0, 164.8, 0,
      // Bar 3: G -> F#
      196.0, 196.0, 0, 196.0, 185.0, 185.0, 0, 185.0, 164.8, 0, 146.8, 0, 123.5, 0, 146.8, 0,
      // Bar 4: Supernova Climax
      220.0, 0, 220.0, 0, 246.9, 0, 246.9, 0, 220.0, 0, 196.0, 0, 185.0, 164.8, 146.8, 123.5
    ];

    const bFreq = cosmicBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.09, 'sawtooth');
    }

    // Cosmic Starburst 16th Arpeggios (B minor celestial pentatonic)
    const starArp = [493.9, 587.3, 659.3, 739.9, 880.0, 987.8];
    const arpIdx = (s % 6);
    this.playLeadTone(time, starArp[arpIdx], 0.07, 'sawtooth');
  }

  // =========================================================================
  // LEVEL 5: ATLANTIS NEON ABYSS (Liquid Cyber-D&B / Ocean Synth-Metal)
  // =========================================================================
  tickAtlantis(time, s) {
    const barStep = s % 16;

    // Fast Liquid D&B Breakbeat: Kick on 0, 10; Snare on 4, 12; Crisp Hi-Hats
    if (barStep === 0 || barStep === 10) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time, 0.28);
    }
    this.playHiHat(time, (barStep % 2 === 0) ? 0.07 : 0.035);

    // Deep Aquatic Sub-Bass (D minor -> F -> C -> Bb) - 73.4 Hz
    const atlantisBass = [
      // Bar 1: D sub
      73.4, 0, 73.4, 0, 0, 73.4, 0, 87.3, 73.4, 0, 73.4, 0, 0, 98.0, 0, 87.3,
      // Bar 2: F
      87.3, 0, 87.3, 0, 0, 87.3, 0, 98.0, 87.3, 0, 110.0, 0, 98.0, 0, 87.3, 0,
      // Bar 3: C
      65.4, 0, 65.4, 0, 0, 65.4, 0, 73.4, 65.4, 0, 87.3, 0, 73.4, 0, 65.4, 0,
      // Bar 4: Bb -> A turnaround
      58.3, 0, 58.3, 0, 0, 58.3, 0, 73.4, 55.0, 0, 55.0, 0, 73.4, 0, 87.3, 0
    ];

    const bFreq = atlantisBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.16, 'sawtooth');
    }

    // Bioluminescent Bubble Arpeggio (D minor pentatonic high register)
    const bubbleArp = [587.3, 698.5, 784.0, 880.0, 1046.5, 1174.7];
    const arpIdx = (s * 2) % 6;
    if (s % 2 === 0) {
      this.playLeadTone(time, bubbleArp[arpIdx], 0.08, 'square');
    }
  }

  // =========================================================================
  // LEVEL 6: DIONYSUS NEON RAVE (Electro-Funk Cyber-Metal)
  // =========================================================================
  tickDionysus(time, s) {
    const barStep = s % 16;

    // Four-on-the-Floor Dance Stomp & Rave Claps
    if (barStep === 0 || barStep === 4 || barStep === 8 || barStep === 12) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time, 0.25);
    }
    // Off-beat open hi-hats for rave groove
    if (barStep % 4 === 2) {
      this.playHiHat(time, 0.1);
    } else {
      this.playHiHat(time, 0.04);
    }

    // Funky Slap Bassline in G minor (98.0 Hz -> 116.5 Hz -> 130.8 Hz)
    const dionysusBass = [
      // Bar 1: G funk bounce
      98.0, 0, 98.0, 116.5, 0, 98.0, 0, 130.8, 98.0, 0, 98.0, 0, 146.8, 0, 130.8, 0,
      // Bar 2: Bb funk
      116.5, 0, 116.5, 0, 130.8, 0, 146.8, 0, 116.5, 0, 130.8, 0, 146.8, 0, 164.8, 0,
      // Bar 3: F funk
      87.3, 0, 87.3, 0, 98.0, 0, 110.0, 0, 87.3, 0, 98.0, 0, 110.0, 0, 130.8, 0,
      // Bar 4: Eb -> D turnaround
      77.8, 0, 77.8, 0, 87.3, 0, 98.0, 0, 73.4, 73.4, 87.3, 87.3, 98.0, 116.5, 130.8, 146.8
    ];

    const bFreq = dionysusBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.11, 'sawtooth');
    }

    // Synth-Brass Riff on bars 2 and 4
    const dionysusLead = [
      // Bar 1
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      // Bar 2: Brass Hook 1
      392.0, 0, 466.2, 0, 523.3, 0, 587.3, 587.3, 0, 0, 523.3, 0, 466.2, 0, 392.0, 0,
      // Bar 3
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      // Bar 4: Rave Climax
      587.3, 0, 698.5, 0, 784.0, 0, 880.0, 0, 784.0, 0, 698.5, 0, 587.3, 523.3, 466.2, 0
    ];

    const lFreq = dionysusLead[s];
    if (lFreq > 0) {
      this.playLeadTone(time, lFreq, 0.15, 'square');
    }
  }

  // =========================================================================
  // LEVEL 7: ARES ORBITAL WAR CITADEL (Industrial Thrash-Metal)
  // =========================================================================
  tickAres(time, s) {
    const barStep = s % 16;

    // Relentless Industrial Double-Kick Blast Beats
    if (barStep % 2 === 0 || barStep === 7 || barStep === 15) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time, 0.35);
    }
    this.playHiHat(time, 0.05);

    // Heavy Thrash Metal Gallop in F# minor (92.5 Hz)
    const aresBass = [
      // Bar 1: F# brutal gallop
      92.5, 92.5, 92.5, 0, 92.5, 92.5, 0, 110.0, 92.5, 92.5, 92.5, 0, 123.5, 0, 110.0, 0,
      // Bar 2: A / G# heavy chug
      110.0, 110.0, 0, 110.0, 103.8, 103.8, 0, 103.8, 92.5, 92.5, 0, 92.5, 123.5, 0, 110.0, 0,
      // Bar 3: D / C# power riffs
      146.8, 146.8, 0, 146.8, 138.6, 138.6, 0, 138.6, 92.5, 92.5, 0, 92.5, 92.5, 92.5, 0, 92.5,
      // Bar 4: Chromatic War Cry
      138.6, 0, 138.6, 0, 130.8, 0, 123.5, 0, 116.5, 0, 110.0, 0, 103.8, 98.0, 92.5, 87.3
    ];

    const bFreq = aresBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.08, 'sawtooth');
    }

    // Heavy Tremolo Guitar War Riff (dissonant tritone harmonics)
    const aresRiff = [370.0, 440.0, 493.9, 523.3, 554.4, 622.3];
    const riffNote = aresRiff[(s + Math.floor(s / 8)) % 6];
    if (s % 2 === 0) {
      this.playLeadTone(time, riffNote, 0.07, 'sawtooth');
    }
  }

  // =========================================================================
  // LEVEL 8: KRONOS CHRONO-SINGULARITY (Neo-Classical Speed-Metal Solo)
  // =========================================================================
  tickChronos(time, s) {
    const barStep = s % 16;

    // Relentless 180 BPM Blast Beats with Accented Snares
    if (barStep === 0 || barStep === 2 || barStep === 6 || barStep === 8 || barStep === 10 || barStep === 14) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time, 0.38);
    }
    this.playHiHat(time, 0.065);

    // Clockwork Chrono Bass in A Harmonic Minor (110 Hz)
    const chronosBass = [
      // Bar 1: A
      110.0, 110.0, 0, 110.0, 110.0, 0, 130.8, 110.0, 110.0, 110.0, 0, 110.0, 146.8, 0, 130.8, 0,
      // Bar 2: F -> E
      174.6, 174.6, 0, 174.6, 164.8, 164.8, 0, 164.8, 174.6, 0, 164.8, 0, 207.7, 0, 164.8, 0,
      // Bar 3: D -> C
      146.8, 146.8, 0, 146.8, 130.8, 130.8, 0, 130.8, 110.0, 0, 130.8, 0, 146.8, 0, 164.8, 0,
      // Bar 4: Final Chrono Climax (G#dim7 -> A)
      207.7, 0, 207.7, 0, 246.9, 0, 246.9, 0, 293.7, 0, 261.6, 0, 246.9, 207.7, 164.8, 110.0
    ];

    const bFreq = chronosBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.08, 'sawtooth');
    }

    // Virtuoso Sweeping Baroque Arpeggios (Vivaldi/Malmsteen style)
    // A minor (440, 523.2, 659.2), E7 (415.3, 493.9, 659.2, 783.9), A harmonic (830.6, 880)
    const baroqueRiff = [
      440.0, 523.2, 659.2, 880.0, 659.2, 523.2,
      415.3, 493.9, 659.2, 830.6, 659.2, 493.9,
      523.2, 659.2, 784.0, 1046.5, 784.0, 659.2,
      830.6, 987.8, 1174.7, 1318.5, 1174.7, 987.8
    ];
    const bNote = baroqueRiff[s % baroqueRiff.length];
    this.playLeadTone(time, bNote, 0.065, 'square');
  }

  // =========================================================================
  // LEVEL 9: RIVER STYX SPECTRAL RUN (152 BPM Gothic Darkwave Doom-Metal)
  // =========================================================================
  tickStyx(time, s) {
    const barStep = s % 16;

    // Heavy syncopated doom beat
    if (barStep === 0 || barStep === 6 || barStep === 8 || barStep === 14) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time, 0.32);
    }
    if (barStep % 2 === 0) {
      this.playHiHat(time, barStep === 10 ? 0.08 : 0.04);
    }

    // Ghostly D-minor Bassline (73.4 Hz root)
    const styxBass = [
      // Bar 1: D
      73.4, 0, 73.4, 73.4, 0, 73.4, 87.3, 0, 73.4, 0, 73.4, 0, 98.0, 0, 87.3, 0,
      // Bar 2: Bb -> C
      116.5, 0, 116.5, 0, 116.5, 116.5, 0, 116.5, 130.8, 0, 130.8, 0, 116.5, 0, 98.0, 0,
      // Bar 3: G -> A
      98.0, 0, 98.0, 0, 98.0, 0, 110.0, 0, 110.0, 0, 110.0, 110.0, 0, 110.0, 116.5, 0,
      // Bar 4: Ghost descent (A -> G# -> G -> F)
      110.0, 0, 103.8, 0, 98.0, 0, 87.3, 0, 73.4, 73.4, 87.3, 98.0, 110.0, 0, 87.3, 0
    ];
    const bFreq = styxBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.12, 'sawtooth');
    }

    // Spectral Wraith Arpeggio (D minor / Phrygian mystery)
    const spectralLead = [
      587.3, 698.5, 880.0, 1046.5, 932.3, 880.0, 698.5, 587.3,
      622.3, 698.5, 880.0, 932.3, 1174.7, 1046.5, 880.0, 698.5
    ];
    if (s % 2 === 0) {
      const lNote = spectralLead[(s / 2) % spectralLead.length];
      this.playLeadTone(time, lNote, 0.09, 'sawtooth');
    }
  }

  // =========================================================================
  // LEVEL 10: APOLLO SOLAR HYPER-DRIVE (172 BPM Radiant Power-Metal)
  // =========================================================================
  tickApollo(time, s) {
    const barStep = s % 16;

    // Galloping Power-Metal Double Bass Drums
    if (barStep === 0 || barStep === 1 || barStep === 2 || barStep === 4 ||
        barStep === 6 || barStep === 8 || barStep === 9 || barStep === 10 ||
        barStep === 12 || barStep === 14) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 12) {
      this.playSnare(time, 0.35);
    } else if (barStep === 15) {
      this.playSnare(time, 0.15); // Ghost snare
    }
    this.playHiHat(time, 0.05);

    // Heroic E-Minor Power Bassline (82.4 Hz root)
    const apolloBass = [
      // Bar 1: E driving 16ths
      82.4, 82.4, 82.4, 0, 82.4, 82.4, 98.0, 0, 82.4, 82.4, 82.4, 0, 110.0, 0, 98.0, 0,
      // Bar 2: C -> D
      130.8, 130.8, 0, 130.8, 130.8, 0, 146.8, 0, 146.8, 146.8, 0, 146.8, 164.8, 0, 146.8, 0,
      // Bar 3: G -> B
      98.0, 98.0, 0, 98.0, 123.5, 0, 123.5, 0, 110.0, 110.0, 0, 110.0, 138.6, 0, 123.5, 0,
      // Bar 4: Solar Flare Ascent
      164.8, 0, 146.8, 0, 130.8, 0, 123.5, 0, 110.0, 123.5, 130.8, 146.8, 164.8, 196.0, 185.0, 164.8
    ];
    const bFreq = apolloBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.075, 'sawtooth');
    }

    // Blazing Solar Shred Lead (DragonForce / Power Metal speed)
    const solarRiff = [
      659.3, 784.0, 987.8, 1174.7, 1318.5, 1174.7, 987.8, 784.0,
      880.0, 1046.5, 1318.5, 1568.0, 1318.5, 1046.5, 880.0, 784.0,
      987.8, 1174.7, 1480.0, 1760.0, 1480.0, 1174.7, 987.8, 880.0,
      1318.5, 1174.7, 987.8, 880.0, 784.0, 659.3, 587.3, 659.3
    ];
    const lNote = solarRiff[s % solarRiff.length];
    this.playLeadTone(time, lNote, 0.055, 'square');
  }

  // =========================================================================
  // LEVEL 11: CHAOS PRIMORDIAL GENESIS (192 BPM Hyper-Speed Cyber-Metal Symphony)
  // =========================================================================
  tickChaos(time, s) {
    const barStep = s % 16;

    // Relentless Cosmic Blast Beats
    if (barStep % 2 === 0 || barStep === 7 || barStep === 15) {
      this.playKick(time);
    }
    if (barStep === 4 || barStep === 7 || barStep === 12 || barStep === 15) {
      this.playSnare(time, 0.4);
    }
    this.playHiHat(time, 0.07);

    // Quantum Antimatter Bassline (Deep C 65.4 Hz with wild harmonic shifts)
    const chaosBass = [
      // Bar 1: C Quantum Pulse
      65.4, 65.4, 0, 65.4, 77.8, 65.4, 92.5, 0, 65.4, 65.4, 0, 65.4, 98.0, 0, 77.8, 0,
      // Bar 2: F# / Diminished Singularity
      92.5, 92.5, 0, 92.5, 110.0, 0, 92.5, 0, 123.5, 123.5, 0, 123.5, 138.6, 0, 123.5, 0,
      // Bar 3: G -> Ab -> B Tension
      98.0, 0, 103.8, 0, 123.5, 0, 130.8, 0, 146.8, 0, 155.6, 0, 164.8, 0, 185.0, 0,
      // Bar 4: Big Bang Creation Tremolo
      196.0, 185.0, 174.6, 164.8, 155.6, 146.8, 138.6, 130.8, 123.5, 110.0, 98.0, 92.5, 87.3, 77.8, 73.4, 65.4
    ];
    const bFreq = chaosBass[s];
    if (bFreq > 0) {
      this.playBassTone(time, bFreq, 0.065, 'sawtooth');
    }

    // Reality-Bending Symphonic Quantum Arpeggios (Multi-octave hyper-speed)
    const chaosArp = [
      523.2, 659.3, 784.0, 1046.5, 1318.5, 1568.0, 1174.7, 987.8,
      622.3, 784.0, 932.3, 1244.5, 1568.0, 1864.7, 1396.9, 1108.7,
      740.0, 880.0, 1108.7, 1480.0, 1760.0, 2217.5, 1661.2, 1318.5,
      987.8, 1174.7, 1396.9, 1760.0, 2093.0, 2489.0, 1975.5, 1568.0
    ];
    const cNote = chaosArp[s % chaosArp.length];
    this.playLeadTone(time, cNote, 0.048, 'square');
  }

  // =========================================================================
  // SYNTH INSTRUMENTS (Pure Web Audio Nodes)
  // =========================================================================
  playKick(time) {
    const ctx = this.audioCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.frequency.setValueAtTime(160, time);
    osc.frequency.exponentialRampToValueAtTime(35, time + 0.12);

    gain.gain.setValueAtTime(0.35, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.14);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(time);
    osc.stop(time + 0.15);
  }

  playSnare(time, vol = 0.22) {
    const ctx = this.audioCtx;
    // Noise buffer
    const bufferSize = ctx.sampleRate * 0.1;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(1000, time);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(vol, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.1);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start(time);
    noise.stop(time + 0.11);
  }

  playHiHat(time, vol = 0.05) {
    const ctx = this.audioCtx;
    const bufferSize = ctx.sampleRate * 0.04;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(6000, time);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(vol, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.04);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start(time);
    noise.stop(time + 0.05);
  }

  playBassTone(time, freq, dur, type = 'sawtooth') {
    const ctx = this.audioCtx;
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, time);

    // Warm punchy lowpass filter
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, time);
    filter.frequency.exponentialRampToValueAtTime(250, time + dur);

    gain.gain.setValueAtTime(0.24, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + dur);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(time);
    osc.stop(time + dur + 0.02);
  }

  playLeadTone(time, freq, dur, type = 'square') {
    const ctx = this.audioCtx;
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, time);

    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2200, time);
    filter.Q.setValueAtTime(2, time);

    gain.gain.setValueAtTime(0.12, time);
    gain.gain.exponentialRampToValueAtTime(0.005, time + dur);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(time);
    osc.stop(time + dur + 0.02);
  }
}
